// /src/GameContainer.tsx (CSV 加载版)

import React, { useState, useEffect, useMemo, useRef } from 'react';
import Papa from 'papaparse';
import { 
  LevelDefinition, WordData, VocabularyEntry, 
  PileData, FoundationPileData, CardState, CardType 
} from './types/game';
import TableauPile from './components/TableauPile';
import CategoryPile from './components/CategoryPile';
import VocabularyResults from './components/VocabularyResults';
import StockWasteArea from './components/StockWasteArea'; 
import { useCrossfadeLoop } from './hooks/useCrossfadeLoop';

const STOCK_PILE_ID = 100; // Changed from 5 to 100 to avoid conflict
const WASTE_PILE_ID = 101; // Changed from 6 to 101
const NUM_TABLEAU_PILES = 4;
const NUM_FOUNDATION_PILES = 3; 
const TOTAL_LEVELS = 100; // Define total levels for selection UI

interface GameState {
  piles: PileData[]; 
  stockPile: PileData; 
  wastePile: PileData; 
  foundationPiles: FoundationPileData[]; 
  completedFoundations: FoundationPileData[]; // Track cleared piles
  vocabularyList: VocabularyEntry[]; 
  isGameWon: boolean; 
  draggedWord: string | null; 
  sourcePileId: number | null; 
  moves: number; 
}

const findCardData = (word: string, map: Map<string, WordData>): WordData | undefined => {
    return map.get(word);
};

const GameContainer: React.FC = () => {
  // 初始化时从 localStorage 读取最大解锁关卡
  const [maxUnlockedLevel, setMaxUnlockedLevel] = useState<number>(() => {
      try {
          const saved = localStorage.getItem('solitaire_vocab_level');
          return saved ? parseInt(saved, 10) : 1;
      } catch (error) {
          // localStorage 可能不可用（如隐私模式、SSR等）
          return 1;
      }
  });

  // Background Music Hook
  const { isPlaying: isMusicPlaying, toggle: toggleMusic, isLoaded: isMusicLoaded, needsUserInteraction, enablePlayback } = useCrossfadeLoop((process.env.PUBLIC_URL || '') + '/audio/background-music.wav', 2);
  
  // Local state to control music permission modal visibility
  const [showMusicModal, setShowMusicModal] = useState(false);
  
  // Show modal when user interaction is needed
  useEffect(() => {
    if (needsUserInteraction && isMusicLoaded) {
      setShowMusicModal(true);
    }
  }, [needsUserInteraction, isMusicLoaded]);

  // 当前游玩的关卡，默认为最大解锁关卡
  const [currentLevelId, setCurrentLevelId] = useState<number>(maxUnlockedLevel);
  
  // 关卡选择菜单状态
  const [showLevelMenu, setShowLevelMenu] = useState<boolean>(false);
  
  const [levelData, setLevelData] = useState<LevelDefinition | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track game start time for analytics
  const gameStartTimeRef = useRef<number | null>(null);

  // 加载 CSV 数据
  useEffect(() => {
    const loadLevelData = async () => {
        setLoading(true); // Reset loading state on level change
        try {
            // Dynamic fetch based on currentLevelId
            const response = await fetch(`${process.env.PUBLIC_URL || ''}/data/level_${currentLevelId}.csv`);
            if (!response.ok) {
                 // Fallback or error handling
                 if (currentLevelId > 1) {
                    throw new Error(`Level ${currentLevelId} not found. (No more levels?)`);
                 }
                 throw new Error('Failed to load level data');
            }
            const csvText = await response.text();
            
            Papa.parse(csvText, {
                header: true,
                dynamicTyping: true, // 自动转换 true/false, numbers
                complete: (results) => {
                    const rawData = results.data as any[];
                    
                    // Track level start
                    if (window.gtag) {
                        window.gtag('event', 'level_start', {
                            level_id: currentLevelId,
                            level_name: `Level ${currentLevelId}`
                        });
                    }
                    
                    // Helper to safely parse boolean
                    const safeBool = (val: any) => {
                        if (typeof val === 'boolean') return val;
                        if (typeof val === 'string') {
                            const lower = val.toLowerCase().trim();
                            return lower === 'true';
                        }
                        return false;
                    };

                    // 转换 CSV 行数据为 WordData
                    const wordSet: WordData[] = rawData
                        .filter(row => row.word) // 过滤空行
                        .map(row => {
                             // 安全解析 targetCount
                             let safeTargetCount = row.targetCount;
                             if (typeof safeTargetCount === 'string' && safeTargetCount.trim() !== '') {
                                 safeTargetCount = parseInt(safeTargetCount, 10);
                             } else if (safeTargetCount === undefined || safeTargetCount === null || safeTargetCount === '') {
                                 // 如果是 PARENT 但没有 targetCount，可以给个默认值 5 (虽然 CSV 应该有)
                                 if (row.type === 'PARENT') safeTargetCount = 5;
                             }

                             return {
                                word: row.word,
                                meaning: row.meaning,
                                categoryId: row.categoryId,
                                type: row.type as CardType,
                                isTarget: safeBool(row.isTarget), 
                                levelId: currentLevelId, 
                                emoji: row.emoji,
                                image: row.image,
                                targetCount: safeTargetCount // 使用安全解析后的值
                            };
                        });

                    // 解析 Initial Layout
                    // 我们需要根据 pileId 和 isFaceUp 重建 initialLayout
                    // 注意：CSV 中 pileId 为空的会被归入 Stock
                    const initialLayout: CardState[] = [];

                    rawData.forEach((row, index) => {
                        if (!row.word) return;
                        if (row.pileId) {
                            // 是 Tableau 的一部分
                            // 注意：这里 stackIndex 需要我们在加载后重新计算，
                            // 或者在 CSV 里就按顺序排好。
                            // 简单起见，我们假设 CSV 里的顺序就是堆叠顺序。
                            initialLayout.push({
                                word: row.word,
                                pile: row.pileId,
                                stackIndex: 0, // 暂占位，后面重算
                                isFaceUp: safeBool(row.isFaceUp)
                            });
                        } 
                    });

                    // 修复：对 initialLayout 进行排序，确保每个 Pile 中，背面朝上的牌（FaceDown）总是在底部（索引较小），
                    // 正面朝上的牌（FaceUp）在顶部。防止 CSV 数据顺序混乱导致显示异常。
                    initialLayout.sort((a, b) => {
                        if (a.pile !== b.pile) return a.pile - b.pile;
                        if (a.isFaceUp === b.isFaceUp) return 0;
                        return a.isFaceUp ? 1 : -1; // false (FaceDown) < true (FaceUp)
                    });

                    // 计算 stackIndex (按 pile 分组后的索引)
                    // 这步很重要，否则所有牌都在 stackIndex 0
                    const pileCounts: Record<number, number> = {};
                    initialLayout.forEach(card => {
                        const currentCount = pileCounts[card.pile] || 0;
                        card.stackIndex = currentCount;
                        pileCounts[card.pile] = currentCount + 1;
                    });

                    // 构建 LevelDefinition
                    // 自动推断 categoryWord (这里简化处理，取第一个 PARENT 的 category)
                    // 自动推断 categoryWord (这里简化处理，取第一个 PARENT 的 category)
                    const parentCard = wordSet.find(w => w.type === 'PARENT');
                    const categoryWord = parentCard ? "Mixed Categories" : "Unknown";

                    const newLevelData: LevelDefinition = {
                        levelId: currentLevelId,
                        categoryWord: categoryWord,
                        categoryEmoji: "🧩",
                        totalTargetWords: wordSet.filter(w => w.isTarget).length,
                        meaning: "加载自 CSV",
                        wordSet: wordSet,
                        initialLayout: initialLayout
                    };

                    setLevelData(newLevelData);
                    setLoading(false);
                },
                error: (err: any) => {
                    setError(err.message);
                    setLoading(false);
                }
            });
        } catch (err: any) {
            setError(err.message);
            setLoading(false);
        }
    };

    loadLevelData();
  }, [currentLevelId]);

  const allWordDataMap = useMemo(() => {
    if (!levelData) return new Map();
    return new Map(levelData.wordSet.map(w => [w.word, w]));
  }, [levelData]);
  
  // 初始化游戏状态 (依赖 levelData)
  const initGame = (data: LevelDefinition): GameState => {
    // 动态计算 Pile 数量，基于 initialLayout 中的最大 pileId
    // 这里需要保证至少有 NUM_TABLEAU_PILES 个，以免渲染出错
    const maxPileId = Math.max(NUM_TABLEAU_PILES, ...data.initialLayout.map(c => c.pile));
    
    const initialPiles: PileData[] = Array.from({ length: maxPileId }, (_, i) => ({
      pileId: i + 1,
      cards: [],
    }));

    // ============ 随机化逻辑开始 ============
    // 1. 收集所有可用的单词 (Words)
    // 注意：我们只打乱 "单词分配到位置" 的过程，
    // 而保留 "位置本身的结构" (即 CSV 定义的多少个 Pile，每个 Pile 有几张牌，哪张朝上)
    const allWords = [...data.wordSet];
    
    // Fisher-Yates 洗牌算法
    for (let i = allWords.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allWords[i], allWords[j]] = [allWords[j], allWords[i]];
    }

    // 2. 提取 CSV 定义的布局骨架 (Slots)
    // 骨架只包含位置信息 (pileId, stackIndex, isFaceUp)，不包含具体单词
    const layoutSlots: { pileId: number; isFaceUp: boolean }[] = [];
    data.initialLayout.forEach(card => {
        if (card.pile) {
            layoutSlots.push({
                pileId: card.pile,
                isFaceUp: card.isFaceUp
            });
        }
    });

    // 3. 将洗乱的单词填入骨架
    // 我们使用前 layoutSlots.length 个单词填入 Tableau
    const cardsInLayout = new Set<string>();
    
    layoutSlots.forEach((slot, index) => {
        const wordData = allWords[index]; // 取一个随机单词
        const pile = initialPiles.find(p => p.pileId === slot.pileId);
        if (pile && wordData) {
            pile.cards.push({
                word: wordData.word,
                pile: slot.pileId,
                stackIndex: pile.cards.length, // 自动计算 stackIndex
                isFaceUp: slot.isFaceUp
            });
            cardsInLayout.add(wordData.word);
        }
    });

    // 4. 剩下的单词放入 Stock (牌库)
    // 过滤掉已经在 Tableau 中的单词
    const stockCards: CardState[] = allWords
        .filter(w => !cardsInLayout.has(w.word))
        .map((w, index) => ({
            word: w.word,
            pile: STOCK_PILE_ID,
            stackIndex: index,
            isFaceUp: false, 
        }));
    
    // Stock 牌其实不需要再洗了，因为 allWords 已经洗过了，
    // 但为了保险起见或者如果过滤逻辑有顺序影响，再洗一次也无妨。
    // 这里直接使用即可。
    // ============ 随机化逻辑结束 ============

    const initialFoundations: FoundationPileData[] = Array.from({ length: NUM_FOUNDATION_PILES }, (_, i) => ({
      foundationId: i + 1,
      categoryWord: null,
      categoryEmoji: null,
      currentCount: 0,
      targetCount: 0, // 初始化为 0
      lockedCategoryId: null,
    }));

    return {
      piles: initialPiles,
      stockPile: { pileId: STOCK_PILE_ID, cards: stockCards },
      wastePile: { pileId: WASTE_PILE_ID, cards: [] },
      foundationPiles: initialFoundations,
      completedFoundations: [],
      vocabularyList: [],
      isGameWon: false,
      draggedWord: null,
      sourcePileId: null,
      moves: 0, 
    };
  };

  // State 需要处理 loading
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // 显示 Toast 并自动消失
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 2000);
  };

  // 当 data 加载完成后初始化 game
  useEffect(() => {
      if (levelData) {
          setGameState(initGame(levelData));
          gameStartTimeRef.current = Date.now(); // Set game start time
      }
  }, [levelData]);

  const handleRestart = () => {
      if (window.confirm("确定要重新开始吗？")) {
          if (levelData) {
             setGameState(initGame(levelData));
             gameStartTimeRef.current = Date.now(); // Reset game start time
             // Track game restart
             if (window.gtag) {
                 window.gtag('event', 'game_restart', {
                     level_id: currentLevelId
                 });
             }
          }
      }
  };

  const handleNextLevel = () => {
      // alert("更多关卡敬请期待！(只需添加 level_2.csv)");
      // setGameState(initGame(levelData));
      const nextLevel = currentLevelId + 1;
      
      // 如果通关的是当前最大解锁关卡，则更新进度
      if (nextLevel > maxUnlockedLevel) {
          setMaxUnlockedLevel(nextLevel);
          // 保存进度到 localStorage
          try {
              localStorage.setItem('solitaire_vocab_level', nextLevel.toString());
          } catch (error) {
              // localStorage 可能不可用，忽略错误
          }
      }
      
      setCurrentLevelId(nextLevel);
      // Track next level (level selection is already tracked in handleLevelSelect)
      // But this is automatic progression, so we can track it separately if needed
      if (window.gtag) {
          window.gtag('event', 'level_progress', {
              from_level: currentLevelId,
              to_level: nextLevel,
              is_unlocked: nextLevel > maxUnlockedLevel
          });
      }
  };

  const handleLevelSelect = (levelId: number) => {
      // Allow selecting any level for testing purposes if needed
      // Or keep the lock logic:
      // if (levelId <= maxUnlockedLevel) {
          setCurrentLevelId(levelId);
          setShowLevelMenu(false);
          // Track level selection
          if (window.gtag) {
              window.gtag('event', 'level_select', {
                  level_id: levelId,
                  previous_level: currentLevelId
              });
          }
      // }
  };

  const handleAddToVocab = (wordData: WordData) => {
    const newEntry: VocabularyEntry = {
      word: wordData.word,
      meaning: wordData.meaning,
      sourceLevel: wordData.levelId,
    };
    
    setGameState(prev => {
      if (!prev) return null;
      const isAlreadyAdded = prev.vocabularyList.some(entry => entry.word === wordData.word);
      if (isAlreadyAdded) {
          showToast(`"${wordData.word}" 已经在生词本里了`);
          return prev; 
      }
      showToast(`已添加 "${wordData.word}" 到生词本`);
      return { ...prev, vocabularyList: [...prev.vocabularyList, newEntry] };
    });
  };

  const handleDrawCard = () => {
      setGameState(prev => {
          if (!prev) return null;
          if (prev.stockPile.cards.length === 0) return prev; 
          const newStockCards = [...prev.stockPile.cards];
          const newWasteCards = [...prev.wastePile.cards];
          const cardToDraw = newStockCards.pop()!;
          
          newWasteCards.push({
              ...cardToDraw,
              pile: WASTE_PILE_ID,
              isFaceUp: true,
              stackIndex: newWasteCards.length,
          });
          
          return {
              ...prev,
              stockPile: { ...prev.stockPile, cards: newStockCards },
              wastePile: { ...prev.wastePile, cards: newWasteCards },
              moves: prev.moves + 1, 
          };
      });
  };

  const handleRecycleStock = () => {
    setGameState(prev => {
          if (!prev) return null;
          if (prev.stockPile.cards.length > 0 || prev.wastePile.cards.length === 0) return prev;
          const newStockCards = prev.wastePile.cards.map((card, index) => ({
              ...card,
              pile: STOCK_PILE_ID,
              isFaceUp: false, 
              stackIndex: index,
          })).reverse(); 

          return {
              ...prev,
              stockPile: { ...prev.stockPile, cards: newStockCards },
              wastePile: { ...prev.wastePile, cards: [] },
              moves: prev.moves + 1, 
          };
      });
  };

  const handleCardDragStart = (word: string, sourcePileId: number) => {
    setGameState(prev => (prev ? { ...prev, draggedWord: word, sourcePileId } : null));
  };

  const getDraggedCardsSequence = (state: GameState, sourceId: number, startWord: string): CardState[] => {
      let sourceCards: CardState[] = [];
      if (sourceId === WASTE_PILE_ID) {
          sourceCards = state.wastePile.cards;
      } else {
          const pile = state.piles.find(p => p.pileId === sourceId);
          if (pile) sourceCards = pile.cards;
      }
      const startIndex = sourceCards.findIndex(c => c.word === startWord);
      if (startIndex === -1) return [];
      return sourceCards.slice(startIndex);
  };

  // Helper to check if a sequence of cards belongs to the same category
  const isSequenceHomogeneous = (sequence: CardState[], map: Map<string, WordData>): boolean => {
      if (sequence.length <= 1) return true;
      const firstData = findCardData(sequence[0].word, map);
      if (!firstData) return false;
      
      return sequence.every(card => {
          const data = findCardData(card.word, map);
          return data && data.categoryId === firstData.categoryId;
      });
  };

  const handleTableauDrop = (draggedWord: string, targetPileId: number) => {
    setGameState(prev => {
      if (!prev || !prev.sourcePileId || prev.sourcePileId === targetPileId) return prev; 

      const targetPile = prev.piles.find(p => p.pileId === targetPileId);
      const draggedCardData = findCardData(draggedWord, allWordDataMap);
      
      if (!targetPile || !draggedCardData) return prev; 

      const draggedSequence = getDraggedCardsSequence(prev, prev.sourcePileId, draggedWord);
      if (draggedSequence.length === 0) return prev;

      // NEW: Ensure the dragged sequence is homogeneous (same category)
      if (draggedSequence.length > 1 && !isSequenceHomogeneous(draggedSequence, allWordDataMap)) {
          return prev;
      }

      const targetTopCard = targetPile.cards[targetPile.cards.length - 1];

      // 如果目标 pile 有牌，需要验证放置规则
      if (targetTopCard) {
          const targetData = findCardData(targetTopCard.word, allWordDataMap);
          if (!targetData) return prev;

          // 情况 1: 分类不匹配
          if (targetData.categoryId !== draggedCardData.categoryId) {
              // CHILD 拖到不匹配的 CHILD
              if (draggedCardData.type === 'CHILD' && targetData.type === 'CHILD') {
                  showToast("分类不匹配，请拖到相同分类的牌堆");
              }
              // CHILD 拖到不匹配的 PARENT
              else if (draggedCardData.type === 'CHILD' && targetData.type === 'PARENT') {
                  showToast("分类不匹配，请拖到相同分类的牌堆");
              }
              // PARENT 拖到不匹配的 CHILD
              else if (draggedCardData.type === 'PARENT' && targetData.type === 'CHILD') {
                  showToast("分类不匹配，请拖到相同分类的牌堆");
              }
              return prev;
          }
          
          // 情况 2: 分类匹配但类型不对 - CHILD 不能放在 PARENT 下方
          if (targetData.type === 'PARENT' && draggedCardData.type === 'CHILD') {
              showToast("不能将单词放在类别牌下方");
              return prev;
          }
      }
      
      // 如果目标 pile 为空，或者验证通过，继续执行放置逻辑

      let newSourcePileData = prev.wastePile.cards;
      let isWaste = prev.sourcePileId === WASTE_PILE_ID;
      
      if (!isWaste) {
          const p = prev.piles.find(pile => pile.pileId === prev.sourcePileId);
          if (p) newSourcePileData = p.cards;
      }
      
      const splitIndex = newSourcePileData.findIndex(c => c.word === draggedWord);
      const newSourceCards = newSourcePileData.slice(0, splitIndex);
      
      if (!isWaste && newSourceCards.length > 0) {
          const newTop = newSourceCards[newSourceCards.length - 1];
          if (!newTop.isFaceUp) newTop.isFaceUp = true;
      }

      const movedCards = draggedSequence.map((c, i) => ({
          ...c,
          pile: targetPileId,
          stackIndex: targetPile.cards.length + i,
          isFaceUp: true 
      }));
      
      const newTargetCards = [...targetPile.cards, ...movedCards];

      const updatedPiles = prev.piles.map(p => {
          if (p.pileId === targetPileId) return { ...p, cards: newTargetCards };
          if (p.pileId === prev.sourcePileId) return { ...p, cards: newSourceCards };
          return p;
      });

      return {
        ...prev,
          piles: updatedPiles,
          wastePile: isWaste ? { ...prev.wastePile, cards: newSourceCards } : prev.wastePile,
          draggedWord: null,
          sourcePileId: null,
          moves: prev.moves + 1
      };
    });
  };
  
  const handleFoundationDrop = (draggedWord: string, targetFoundationId: number) => {
    setGameState(prev => {
      if (!prev || !prev.sourcePileId) return prev;
      
      const draggedCardData = findCardData(draggedWord, allWordDataMap);
      const targetFoundation = prev.foundationPiles.find(f => f.foundationId === targetFoundationId);
      
      if (!targetFoundation || !draggedCardData) return prev;

      const draggedSequence = getDraggedCardsSequence(prev, prev.sourcePileId, draggedWord);

      // NEW: Ensure the dragged sequence is homogeneous
      if (!isSequenceHomogeneous(draggedSequence, allWordDataMap)) return prev;

      if (targetFoundation.currentCount === 0) {
          // CHILD 拖到空的目标区
          if (draggedCardData.type !== 'PARENT') {
              showToast("请先放置类别牌（大词）");
              return prev;
          }
          if (draggedSequence.length > 1) return prev;
      } else {
          // 从抽牌区或 pile 拖到不匹配的目标区（PARENT 或 CHILD）
          if (targetFoundation.lockedCategoryId !== draggedCardData.categoryId) {
              showToast("分类不匹配，请拖到相同分类的目标区");
              return prev;
          }
          if (draggedCardData.type === 'PARENT') return prev;
      }

      let newSourcePileData = prev.wastePile.cards;
      let isWaste = prev.sourcePileId === WASTE_PILE_ID;
      if (!isWaste) {
          const p = prev.piles.find(pile => pile.pileId === prev.sourcePileId);
          if (p) newSourcePileData = p.cards;
      }

      const splitIndex = newSourcePileData.findIndex(c => c.word === draggedWord);
      const newSourceCards = newSourcePileData.slice(0, splitIndex);
      
      if (!isWaste && newSourceCards.length > 0) {
          const newTop = newSourceCards[newSourceCards.length - 1];
          if (!newTop.isFaceUp) newTop.isFaceUp = true;
      }

      const newFoundation = {
          ...targetFoundation,
          currentCount: targetFoundation.currentCount + draggedSequence.length,
          lockedCategoryId: targetFoundation.lockedCategoryId || draggedCardData.categoryId,
          categoryWord: targetFoundation.categoryWord || draggedCardData.word, 
          categoryEmoji: targetFoundation.categoryEmoji || draggedCardData.emoji || null,
          targetCount: targetFoundation.targetCount || draggedCardData.targetCount, // 更新 targetCount
      };

      const newFoundations = prev.foundationPiles.map(f => 
          f.foundationId === targetFoundationId ? newFoundation : f
      );

      const updatedPiles = prev.piles.map(p => 
          p.pileId === prev.sourcePileId ? { ...p, cards: newSourceCards } : p
      );

      const totalPlacedCurrent = newFoundations.reduce((sum, f) => sum + f.currentCount, 0);
      const totalPlacedCompleted = prev.completedFoundations.reduce((sum, f) => sum + (f.targetCount || 0), 0);
      const totalPlaced = totalPlacedCurrent + totalPlacedCompleted;
      const isWon = levelData ? totalPlaced === levelData.totalTargetWords : false;

      // Track level completion
      if (isWon && !prev.isGameWon && window.gtag) {
          const timeSpent = gameStartTimeRef.current 
              ? Math.floor((Date.now() - gameStartTimeRef.current) / 1000) 
              : 0;
          window.gtag('event', 'level_complete', {
              level_id: currentLevelId,
              moves: prev.moves + 1,
              time_spent: timeSpent
          });
      }

      return { 
          ...prev, 
          piles: updatedPiles, 
          wastePile: isWaste ? { ...prev.wastePile, cards: newSourceCards } : prev.wastePile,
          foundationPiles: newFoundations, 
          isGameWon: isWon,
          draggedWord: null, 
          sourcePileId: null,
          moves: prev.moves + 1, 
      };
    });
  };

  // 自动清除已完成的 Pile
  useEffect(() => {
    if (!gameState || !levelData) return;

    const fullPiles = gameState.foundationPiles.filter(p => p.targetCount && p.currentCount >= p.targetCount);
    
    if (fullPiles.length > 0) {
        const timer = setTimeout(() => {
            setGameState(prev => {
                if (!prev) return null;
                
                // 再次检查，防止状态在等待期间发生变化
                const pilesToClear = prev.foundationPiles.filter(p => p.targetCount && p.currentCount >= p.targetCount);
                if (pilesToClear.length === 0) return prev;

                const newCompleted = [...prev.completedFoundations, ...pilesToClear];
                
                // 重置已完成的 slot
                const newFoundations = prev.foundationPiles.map(p => {
                    if (p.targetCount && p.currentCount >= p.targetCount) {
                        return {
                            ...p,
                            categoryWord: null,
                            categoryEmoji: null,
                            currentCount: 0,
                            targetCount: 0,
                            lockedCategoryId: null
                        };
                    }
                    return p;
                });

                // 重新计算胜利条件 (防止自动清除时改变 isGameWon 状态，虽然通常应该是保持 True)
                const totalPlacedCurrent = newFoundations.reduce((sum, f) => sum + f.currentCount, 0);
                const totalPlacedCompleted = newCompleted.reduce((sum, f) => sum + (f.targetCount || 0), 0);
                const isWon = levelData ? (totalPlacedCurrent + totalPlacedCompleted) === levelData.totalTargetWords : false;

                return {
                    ...prev,
                    foundationPiles: newFoundations,
                    completedFoundations: newCompleted,
                    isGameWon: isWon
                };
            });
        }, 800); // 0.8秒延迟，让用户看到完成状态

        return () => clearTimeout(timer);
    }
  }, [gameState, levelData]);

  // 移动 Early Return 到 Hook 之后
  if (loading) return <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh'}}>Loading Level Data...</div>;
  if (error) return <div style={{color:'red'}}>Error: {error}</div>;
  if (!gameState || !levelData) return null;

  // 点击卡片处理函数（保留接口兼容性，但不执行任何操作）
  const handleCardClick = (clickedWord: string, sourcePileId: number) => {
    // 空函数，保留接口兼容性
  };
  
  const handleEnableMusic = async () => {
    const success = await enablePlayback();
    if (success) {
      setShowMusicModal(false);
      // Start playing after enabling
      if (!isMusicPlaying) {
        toggleMusic();
        // Track music enabled
        if (window.gtag) {
          window.gtag('event', 'music_toggle', {
            action: 'enable',
            level_id: currentLevelId
          });
        }
      }
    }
  };
  
  const handleMusicToggle = () => {
    toggleMusic();
    // Track music toggle
    if (window.gtag) {
      window.gtag('event', 'music_toggle', {
        action: isMusicPlaying ? 'off' : 'on',
        level_id: currentLevelId
      });
    }
  };

  return (
    <div className="game-container">
        {/* Toast Notification */}
        {toastMessage && (
            <div style={{
                position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px 20px',
                borderRadius: '20px', zIndex: 2000, fontSize: '14px'
            }}>
                {toastMessage}
            </div>
        )}

        {/* Music Auto-play Permission Modal */}
        {showMusicModal && (
            <div style={{
                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 4000
            }}>
                <div style={{
                    backgroundColor: 'white', padding: '30px', borderRadius: '15px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)', maxWidth: '400px', width: '90%',
                    textAlign: 'center'
                }}>
                    <h3 style={{color: '#4CAF50', marginBottom: '15px'}}>🎵 背景音乐</h3>
                    <p style={{marginBottom: '20px', color: '#666'}}>
                        浏览器需要您的许可才能自动播放背景音乐
                    </p>
                    <div style={{display: 'flex', justifyContent: 'center', gap: '15px'}}>
                        <button 
                            onClick={handleEnableMusic}
                            style={{
                                padding: '10px 25px', borderRadius: '5px', border: 'none',
                                backgroundColor: '#4CAF50', color: 'white', cursor: 'pointer', 
                                fontSize: '16px', fontWeight: 'bold'
                            }}
                        >
                            启用音乐
                        </button>
                        <button 
                            onClick={() => setShowMusicModal(false)}
                            style={{
                                padding: '10px 25px', borderRadius: '5px', border: '1px solid #ccc',
                                backgroundColor: '#f0f0f0', cursor: 'pointer', fontSize: '16px'
                            }}
                        >
                            稍后再说
                        </button>
                    </div>
                </div>
            </div>
        )}

        <div className="header-top">
            <span 
                className="level-display" 
                onClick={() => setShowLevelMenu(true)}
                title="Click to select level"
            >
                Level {levelData.levelId}
            </span>
            <div className="moves-display">
                <span>Moves</span>
                <span className="moves-value">{gameState.moves}</span>
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
                {/* Music Button */}
                <button 
                    className="music-btn"
                    onClick={handleMusicToggle}
                    title={isMusicPlaying ? "Pause Music" : "Play Music"}
                    style={{ opacity: isMusicLoaded ? 1 : 0.5, cursor: isMusicLoaded ? 'pointer' : 'default' }}
                >
                    {isMusicPlaying ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18V5l12-2v13" />
                            <circle cx="6" cy="18" r="3" />
                            <circle cx="18" cy="16" r="3" />
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="1" y1="1" x2="23" y2="23" />
                            <path d="M9 18V5l12-2v13" />
                            <circle cx="6" cy="18" r="3" />
                            <circle cx="18" cy="16" r="3" />
                        </svg>
                    )}
                </button>

                {/* Restart Button */}
                <button 
                    className="restart-btn"
                    onClick={handleRestart}
                    title="Restart Game"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 4v6h-6" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                </button>
            </div>
      </div>
      
      {/* Level Selection Modal */}
      {showLevelMenu && (
          <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
              zIndex: 3000
          }} onClick={() => setShowLevelMenu(false)}>
              <div style={{
                  backgroundColor: 'white', padding: '30px', borderRadius: '15px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)', maxWidth: '500px', width: '90%',
                  textAlign: 'center', maxHeight: '80vh', overflowY: 'auto'
              }} onClick={e => e.stopPropagation()}>
                  <h2 style={{color: '#4CAF50', marginBottom: '20px'}}>Select Level</h2>
                  
                  <div className="level-selector-grid">
                      {Array.from({ length: TOTAL_LEVELS }, (_, i) => i + 1).map(level => {
                          const isUnlocked = level <= maxUnlockedLevel;
                          const isCurrent = level === currentLevelId;
                          return (
                              <div 
                                  key={level}
                                  className={`level-btn ${isUnlocked ? 'unlocked' : ''} ${isCurrent ? 'current' : ''}`}
                                  onClick={() => handleLevelSelect(level)}
                              >
                                  {level}
                                  {level > maxUnlockedLevel && <span style={{fontSize: '12px', marginLeft: '5px'}}>🔒</span>}
                              </div>
                          );
                      })}
                  </div>

                  <button 
                      onClick={() => setShowLevelMenu(false)}
                      style={{
                          marginTop: '20px', padding: '10px 20px', borderRadius: '5px', 
                          border: '1px solid #ccc', backgroundColor: '#f0f0f0', cursor: 'pointer'
                      }}
                  >
                      Close
                  </button>
              </div>
          </div>
      )}

      <div className="category-area">
            {/* Top Row: Stock and Waste */}
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginBottom: '10px' }}>
                <StockWasteArea
                    stockPile={gameState.stockPile}
                    wastePile={gameState.wastePile}
                    allWordData={allWordDataMap}
                    onDrawCard={handleDrawCard}
                    onRecycleStock={handleRecycleStock}
                    onCardMoveStart={handleCardDragStart}
                    onCardClick={handleCardClick} 
                    onAddToVocab={handleAddToVocab} 
                />
            </div>

            {/* Bottom Row: Foundation Piles */}
            <div className="foundation-piles-container">
                {gameState.foundationPiles.map(pile => (
                    <CategoryPile
                        key={pile.foundationId}
                        pile={pile}
                        mainCategoryId={levelData.categoryWord.toUpperCase()}
                        onCardDrop={handleFoundationDrop}
                    />
                ))}
            </div>
      </div>

      <div className="tableau-area">
        {gameState.piles.map(pile => (
          <TableauPile 
            key={pile.pileId}
            pile={pile}
            allWordData={allWordDataMap}
                    onCardMoveStart={handleCardDragStart} 
                    onCardDrop={handleTableauDrop} 
                    onCardClick={handleCardClick}
                    onAddToVocab={handleAddToVocab} 
          />
        ))}
      </div>
      
        {/* Victory Modal */}
      {gameState.isGameWon && (
            <div style={{
                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 3000
            }}>
                <div style={{
                    backgroundColor: 'white', padding: '30px', borderRadius: '15px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)', maxWidth: '500px', width: '90%',
                    textAlign: 'center'
                }}>
                    <h2 style={{color: '#4CAF50', marginBottom: '20px'}}>🎉 恭喜通关！</h2>
                    <p>您用了 {gameState.moves} 步完成了挑战。</p>
                    
                    {/* 生词本内容 */}
        <VocabularyResults vocabularyList={gameState.vocabularyList} />
                    
                    <div style={{marginTop: '30px', display: 'flex', justifyContent: 'center', gap: '15px'}}>
                        <button 
                            onClick={handleRestart}
                            style={{
                                padding: '10px 20px', borderRadius: '5px', border: '1px solid #ccc',
                                backgroundColor: '#f0f0f0', cursor: 'pointer', fontSize: '16px'
                            }}
                        >
                            再玩一次
                        </button>
                        <button 
                            onClick={handleNextLevel}
                            style={{
                                padding: '10px 20px', borderRadius: '5px', border: 'none',
                                backgroundColor: '#4CAF50', color: 'white', cursor: 'pointer', fontSize: '16px'
                            }}
                        >
                            下一关 ➡️
                        </button>
                    </div>
                </div>
            </div>
      )}
    </div>
  );
};

export default GameContainer;
